import React from 'react';
import {
    Box, TextField, Button, MenuItem, Grid, Alert, Typography, Divider, Chip, Stack, InputAdornment
} from '@mui/material';
import {
    Save as SaveIcon,
    Payment as PaymentIcon,
    ReceiptLong as ReceiptLongIcon,
    Notes as NotesIcon
} from '@mui/icons-material';
import MenuItemMUI from '@mui/material/MenuItem';
import PersianDatePicker from '../../../common/PersianDatePicker';
import { toPersianDigits, parseNumericInput } from '../../../../utils/convertNumbers';

export default function OperationForm({
    selectedEnrollment,
    opForm,
    opError,
    opSubmitting,
    isManager,
    onFormChange,
    onSubmit
}) {
    if (!selectedEnrollment) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                    برای ثبت عملیات، یکی از کارت‌های هنرجو را انتخاب کنید.
                </Typography>
            </Box>
        );
    }

    const remained = selectedEnrollment.remained_sessions;
    const sessionStatus = remained < 0 ? 'negative' : (remained === 0 ? 'zero' : 'positive');
    const teacherFullName = selectedEnrollment.course.teacher
        ? `${selectedEnrollment.course.teacher.first_name} ${selectedEnrollment.course.teacher.last_name}`.trim()
        : '-';
    const registrationSessions = Number(selectedEnrollment?.course?.registration_sessions || 0);
    const finalFee = Number(selectedEnrollment?.final_fee || 0);
    const sessionCount = Number(opForm.session_count || 0);
    const computedProratedAmount =
        registrationSessions > 0 && sessionCount > 0
            ? Math.round((finalFee * sessionCount) / registrationSessions)
            : null;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {`${selectedEnrollment.student.first_name} ${selectedEnrollment.student.last_name}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedEnrollment.course.course_name} — مدرس: {teacherFullName}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                        size="small"
                        label={`شهریه: ${toPersianDigits(new Intl.NumberFormat('fa-IR').format(Number(selectedEnrollment.final_fee) || 0))} تومان`}
                        variant="outlined"
                        color="primary"
                    />
                    <Chip
                        size="small"
                        label={`به ازای هر ثبت‌نام: ${
                            registrationSessions !== null && registrationSessions !== undefined
                                ? toPersianDigits(String(registrationSessions))
                                : '-'
                        } جلسه اضافه می‌شود`}
                        variant="outlined"
                        color="info"
                    />
                    {selectedEnrollment.discount_amount ? (
                        <Chip
                            size="small"
                            label={`تخفیف: ${toPersianDigits(new Intl.NumberFormat('fa-IR').format(Number(selectedEnrollment.discount_amount) || 0))} تومان`}
                            variant="outlined"
                            color="secondary"
                        />
                    ) : null}
                    <Chip
                        size="small"
                            label={`جلسات باقی‌مانده: ${remained !== null && remained !== undefined ? toPersianDigits(String(remained)) : '-'}`}
                        color={sessionStatus === 'negative' ? 'error' : (sessionStatus === 'zero' ? 'warning' : 'success')}
                        variant="outlined"
                    />
                </Stack>
            </Box>
            <Divider sx={{ mb: 2, opacity: 0.12 }} />
            {opError && <Alert severity="error" sx={{ mb: 2 }}>{opError}</Alert>}
            
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-start">
                        {/* Col 1, Row 1: وضعیت */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                select
                                label="وضعیت"
                                value={opForm.status}
                                onChange={(e) => onFormChange('status', e.target.value)}
                                helperText="نوع عملیات را انتخاب کنید"
                            >
                                <MenuItemMUI value={1}>ثبت نام</MenuItemMUI>
                                <MenuItemMUI value={2}>حاضر</MenuItemMUI>
                                <MenuItemMUI value={3}>غائب</MenuItemMUI>
                                <MenuItemMUI value={4}>مرخصی</MenuItemMUI>
                                <MenuItemMUI value={5}>کنسل</MenuItemMUI>
                                <MenuItemMUI value={7}>جلسه رایگان</MenuItemMUI>
                                <MenuItemMUI value={8}>کسر جلسه (بدون بازگشت وجه)</MenuItemMUI>
                                <MenuItemMUI value={9}>کسر جلسه با بازگشت شهریه</MenuItemMUI>
                                <MenuItemMUI value={10}>ثبت نام با تعداد جلسات دلخواه</MenuItemMUI>
                            </TextField>
                        </Grid>

                        {/* Col 2, Row 1: تاریخ */}
                        <Grid item xs={12} sm={6}>
                            <PersianDatePicker
                                label="تاریخ"
                                value={opForm.date_belonged ? new Date(opForm.date_belonged) : null}
                                onChange={(newDate) => {
                                    if (newDate && !isNaN(newDate.getTime())) {
                                        const formatted = newDate.toISOString().split('T')[0];
                                        onFormChange('date_belonged', formatted);
                                    } else {
                                        onFormChange('date_belonged', '');
                                    }
                                }}
                                readOnly={false}
                            />
                        </Grid>

                        {/* Row 2 (shown when status is financial: 1=registration, 9=refund, 10=custom registration) */}
                        {[1, 9, 10].includes(Number(opForm.status)) && (
                            <>
                                {/* Col 1, Row 2: تعداد جلسه (only for 9/10) */}
                                {[9, 10].includes(Number(opForm.status)) && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="تعداد جلسه"
                                            value={opForm.session_count ?? ''}
                                            onChange={(e) => onFormChange('session_count', e.target.value)}
                                            helperText="تعداد جلسات مورد نظر"
                                        />
                                    </Grid>
                                )}

                                {/* Col 1, Row 2: نحوه پرداخت */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        select
                                        label="نحوه پرداخت"
                                        value={opForm.payment_method}
                                        onChange={(e) => onFormChange('payment_method', e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PaymentIcon fontSize="small" />
                                                </InputAdornment>
                                            )
                                        }}
                                        helperText="روش پرداخت شهریه"
                                    >
                                        <MenuItemMUI value={1}>نقدی</MenuItemMUI>
                                        <MenuItemMUI value={2}>کارت خوان</MenuItemMUI>
                                        <MenuItemMUI value={3}>انتقال به حساب</MenuItemMUI>
                                        <MenuItemMUI value={4}>سایر</MenuItemMUI>
                                    </TextField>
                                </Grid>

                                {/* Amount hint for custom sessions / refunds */}
                                {[9, 10].includes(Number(opForm.status)) && (
                                    <Grid item xs={12}>
                                        <Alert severity={Number(opForm.status) === 9 ? "warning" : "info"} sx={{ py: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {Number(opForm.status) === 9 ? "مبلغ قابل بازگشت:" : "مبلغ قابل دریافت:"}{" "}
                                                {computedProratedAmount !== null ? `${toPersianDigits(new Intl.NumberFormat('fa-IR').format(computedProratedAmount))} تومان` : "-"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                محاسبه بر اساس «شهریه دوره ÷ تعداد جلسات ثبت‌نام دوره × تعداد جلسات انتخاب‌شده» (گرد کردن به نزدیک‌ترین تومان)
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                )}

                                {/* Col 2, Row 2: شماره پیگیری - only show for non-cash payments */}
                                {Number(opForm.payment_method) !== 1 && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            type="text"
                                            inputMode="numeric"
                                            label="شماره پیگیری"
                                            InputLabelProps={{ shrink: true }}
                                            value={opForm.payment_number ? toPersianDigits(opForm.payment_number) : ''}
                                            onChange={(e) => {
                                                const rawValue = e.target.value;
                                                const normalizedValue = parseNumericInput(rawValue);
                                                onFormChange('payment_number', normalizedValue);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <ReceiptLongIcon fontSize="small" />
                                                    </InputAdornment>
                                                )
                                            }}
                                            helperText="در صورت وجود، شماره رسید/پیگیری"
                                        />
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
                {/* Session count for: جلسه رایگان / کسر جلسه */}
                {[7, 8].includes(Number(opForm.status)) && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="تعداد جلسه"
                            value={opForm.session_count ?? ''}
                            onChange={(e) => onFormChange('session_count', e.target.value)}
                            helperText="تعداد جلسات مورد نظر"
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <TextField 
                        fullWidth 
                        size="small" 
                        label="توضیحات" 
                        value={opForm.comment} 
                        onChange={(e) => onFormChange('comment', e.target.value)} 
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <NotesIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        placeholder="در صورت نیاز توضیحی بنویسید"
                    />
                </Grid>
            </Grid>
            <Box sx={{ position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2, pt: 2, backgroundColor: 'background.default', borderTop: '1px solid', borderColor: 'border.main' }}>
                <Button onClick={onSubmit} variant="contained" disabled={opSubmitting || !opForm.status} startIcon={<SaveIcon />}>
                    ثبت
                </Button>
            </Box>
        </Box>
    );
}

